import { Divider, InputNumber, Modal, Typography } from 'antd';
import React, { useEffect, useState } from 'react';

const { Text, Title } = Typography;

export interface AddToPurchaseOrderPartInfo {
  name: string;
  number?: string;
  reference?: string;
  type?: string;
}

export interface AddToPurchaseOrderContext {
  registrationNumber?: string;
  vin?: string;
  supplierName?: string;
}

interface IProps {
  open: boolean;
  part: AddToPurchaseOrderPartInfo;
  context?: AddToPurchaseOrderContext;
  initialQty: number;
  loading?: boolean;
  onConfirm: (qty: number) => void;
  onCancel: () => void;
}

const row = (label: string, value?: string) =>
  value ? (
    <div
      key={label}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 6 }}
    >
      <Text type="secondary" style={{ fontSize: 13, flexShrink: 0 }}>
        {label}
      </Text>
      <Text style={{ fontSize: 13, fontWeight: 500, textAlign: 'right' }}>{value}</Text>
    </div>
  ) : null;

export const AddToPurchaseOrderModal: React.FC<IProps> = ({
  open,
  part,
  context,
  initialQty,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [qty, setQty] = useState<number>(initialQty);

  // Reset qty each time the modal opens with a new part
  useEffect(() => {
    if (open) setQty(initialQty);
  }, [open, initialQty]);

  const isQtyInvalid = !qty || qty <= 0;

  const details = [
    row('Part number', part.number),
    row('Reference', part.reference),
    row('Type', part.type),
    row('Registration', context?.registrationNumber),
    row('VIN', context?.vin),
    row('Supplier', context?.supplierName),
  ].filter(Boolean);

  return (
    <Modal
      title="Add to Purchase Orders"
      open={open}
      onOk={() => !isQtyInvalid && onConfirm(qty)}
      onCancel={onCancel}
      okText="Add to Purchase Orders"
      okButtonProps={{ disabled: isQtyInvalid, loading }}
      cancelButtonProps={{ disabled: loading }}
      width={420}
      destroyOnClose
    >
      <Title level={5} style={{ margin: '0 0 12px' }}>
        {part.name}
      </Title>

      {details.length > 0 && (
        <>
          <div style={{ marginBottom: 12 }}>{details}</div>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}

      <div>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>
          Quantity to Order
        </Text>
        <InputNumber
          min={0}
          step={1}
          precision={2}
          value={qty}
          onChange={(v) => setQty(v ?? 0)}
          style={{ width: '100%' }}
          status={isQtyInvalid ? 'error' : undefined}
          autoFocus
          placeholder="Enter quantity"
        />
        {isQtyInvalid && (
          <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
            Quantity must be greater than 0
          </Text>
        )}
      </div>
    </Modal>
  );
};
